import TagManager from 'react-gtm-module';
import type { Tracker } from '../../types';
import type { GdprSettings } from '@/providers/Cookie/types';
import { initializeGtmConsent, updateGtmConsent } from './gtm-consent';

type MarketingMapping = {
  /** GA4-style event name pushed to the dataLayer. */
  event: string;
  /** Optional transform of the product event's props into marketing params. */
  mapParams?: (props: Record<string, unknown>) => Record<string, unknown>;
};

/**
 * Product events that are ALSO marketing conversions, keyed by the product
 * event name fired through TrackingService. The value is the GA4-style event
 * pushed to the dataLayer; GTM then translates it to each platform's standard
 * event (Meta CompleteRegistration, TikTok CompleteRegistration, ...) in the
 * tag config. Product events not listed here are ignored by GTM — they still
 * reach PostHog.
 *
 * This is the "mapping" half of the hybrid: clean semantic events flow through
 * here automatically.
 */
const MARKETING_EVENT_MAP: Record<string, MarketingMapping> = {
  sign_up: { event: 'sign_up' },
  sign_in: { event: 'sign_in' },
  volt_zero_shown: { event: 'add_to_cart' },
  final_offer_summary_shown: { event: 'initiate_checkout' },
  contract_signed: { event: 'purchase' },
  onboarding_completed: { event: 'onboarding_completed' }
};

/**
 * Button `trackingId`s for app-download / continue-in-app buttons. A `click`
 * on any of these is forwarded to GTM as a `generate_lead` marketing event
 * (Meta `Lead` / TikTok `Download`), with `source` set to the specific
 * element_id. PostHog still receives the raw click with its own element_id.
 *
 * This is the click-based half of the hybrid: marketing lives in the tracking
 * system, so any download button just needs its `trackingId` registered here —
 * no per-call-site marketing code.
 *
 * Keep in sync with the `trackingId` props on the download buttons.
 */
const DOWNLOAD_TRACKING_IDS = new Set<string>([
  'download_volt_app', // DownloadVoltAppBanner
  'no_offer_download_volt', // NoOfferAvailablePage
  'download_app_continue_in_app', // ContinueInAppModal
  'cnp_in_use_verify_in_app', // CnpInUseModal
  'header_continue_in_app_mobile', // Header (mobile)
  'header_continue_in_app_desktop', // Header (desktop)
  'active_loan_download_volt_app', // ActiveLoanPage
  'email_link_validation_open_app', // EmailLinkValidationPage
]);

/**
 * Google Tag Manager tracker.
 *
 * Loads the GTM container imperatively (via react-gtm-module) so Consent Mode
 * defaults are pushed to the dataLayer *before* the container loads — no
 * marketing tag fires before consent.
 *
 * GTM is the source of truth for marketing campaigns (Meta, TikTok, Google
 * Ads). It pushes `page_view` on every route change and forwards the marketing
 * subset of product events: semantic events via MARKETING_EVENT_MAP and
 * app-download clicks via DOWNLOAD_TRACKING_IDS. All other product telemetry
 * (other clicks, sign-in) goes to PostHog, not here.
 */
export class GtmTracker implements Tracker {
  private gtmId: string;
  private isInitialized = false;

  constructor(gtmId: string) {
    this.gtmId = gtmId;
  }

  init(): void {
    if (this.isInitialized) {
      return;
    }

    // Deny all optional consent categories before the container loads.
    initializeGtmConsent();

    TagManager.initialize({ gtmId: this.gtmId });

    this.isInitialized = true;
  }

  trackPageView(props?: Record<string, unknown>): void {
    if (!this.isInitialized) {
      return;
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'page_view',
        page_path: (props?.page_path as string) ?? window.location.pathname,
        page_title: (props?.page_title as string) ?? document.title,
        page_location:
          (props?.page_location as string) ?? window.location.href,
      },
    });
  }

  trackEvent(eventName: string, props?: Record<string, unknown>): void {
    if (!this.isInitialized) {
      return;
    }

    // Semantic marketing events (e.g. sign_up).
    const mapping = MARKETING_EVENT_MAP[eventName];
    if (mapping) {
      TagManager.dataLayer({
        dataLayer: {
          event: mapping.event,
          ...(mapping.mapParams ? mapping.mapParams(props ?? {}) : props),
        },
      });
      return;
    }

    // App-download / continue-in-app clicks → generate_lead.
    if (eventName === 'click') {
      const elementId = props?.element_id;
      if (typeof elementId === 'string' && DOWNLOAD_TRACKING_IDS.has(elementId)) {
        TagManager.dataLayer({
          dataLayer: { event: 'generate_lead', source: elementId },
        });
      }
    }

    // Anything else is not a marketing event — ignored by GTM (PostHog only).
  }

  updateConsent(consents: GdprSettings['consents']): void {
    updateGtmConsent(consents);
  }
}
